import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { QuickBadge } from "@/components/QuickBadge";
import { useWorkerJob } from "@/lib/workerJob";
import { useLeaveVisible } from "@/hooks/useLeaveVisible";
import InstallTip from "@/components/InstallTip";

function StageList({ pid }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get(`/my/projects/${pid}/stages`).then(r => setData(r.data)).catch(() => setData({ stages: [], tasks: [] }));
  }, [pid]);
  if (!data) return <div className="text-sm opacity-60">Loading stages…</div>;
  if (!data.stages.length) return <div className="text-sm opacity-60" data-testid="worker-no-stages">No stages set up for this project yet.</div>;
  return (
    <ol className="space-y-2" data-testid="worker-stage-list">
      {data.stages.map((s, i) => {
        const tasks = data.tasks.filter(t => t.stage_id === s.id);
        return (
          <li key={s.id} className="border border-neutral-800 rounded px-3 py-2" data-testid={`worker-stage-${s.id}`}>
            <div className="text-silver">{i + 1}. {s.name}</div>
            {tasks.length > 0 && <ul className="mt-1 text-sm opacity-80 list-disc pl-5">{tasks.map(t => <li key={t.id}>{t.name}</li>)}</ul>}
          </li>
        );
      })}
    </ol>
  );
}

export default function WorkerProjects() {
  const nav = useNavigate();
  const [projects, setProjects] = useState(null);
  const [job, setJob] = useWorkerJob();
  const leaveOn = useLeaveVisible();
  useEffect(() => { api.get("/my/projects").then(r => setProjects(r.data)).catch(() => setProjects([])); }, []);
  const sel = projects?.find(p => p.id === job);

  return (
    <div className="space-y-4" data-testid="worker-projects">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-2xl">Projects</h1>
        {leaveOn && <button className="btn-ghost text-sm px-4 py-2" onClick={() => nav("/my/leave")} data-testid="btn-request-leave-home">Request leave</button>}
      </div>
      <InstallTip />
      {projects === null && <div className="opacity-60 text-sm">Loading…</div>}
      {projects?.length === 0 && <div className="card opacity-70 text-sm" data-testid="worker-no-projects">There are no open projects right now.</div>}
      {projects?.length > 0 && (
        <ul className="card !p-0 divide-y divide-neutral-800">
          {projects.map(p => (
            <li key={p.id}>
              <button type="button" onClick={() => setJob(p.id)} data-testid={`worker-job-${p.id}`}
                className={`w-full text-left px-4 py-3 flex gap-3 items-start transition-colors ${p.id === job ? "bg-neutral-900 border-l-4 border-silver" : "hover:bg-neutral-900 border-l-4 border-transparent"}`}>
                <span className="text-silver font-bold min-w-[3.5rem]">{p.job_number || "—"}</span>
                <span className="flex-1">
                  <span className="block">{p.name}<QuickBadge p={p}/></span>
                  <span className="block text-xs opacity-60">{p.address || "No site address"}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {sel && (
        <div className="card space-y-3" data-testid="worker-job-detail">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div>
              <h2 className="text-silver text-lg">{sel.job_number ? `${sel.job_number} · ` : ""}{sel.name}</h2>
              <div className="text-xs opacity-60">{sel.address}</div>
            </div>
            <div className="flex gap-2">
              <button className="btn-silver text-sm" onClick={() => nav("/my/timesheets")} data-testid="worker-go-timesheets">Log hours</button>
              <button className="btn-ghost text-sm" onClick={() => nav("/my/hs")} data-testid="worker-go-hs">H&amp;S</button>
            </div>
          </div>
          <h3 className="text-sm opacity-80">Stages</h3>
          <StageList pid={sel.id} />
        </div>
      )}
    </div>
  );
}
